<div class="daily-info-panel">
  <!-- Route info header -->
  <div class="daily-info-header">
    <div class="flex items-center gap-2 text-xs font-medium text-white">
      <i class="pi pi-calendar"></i>
      <span>{{ selectedDate | date: 'EEEE d MMMM yyyy' }}</span>
    </div>
    <div class="flex items-center gap-2 text-xs text-neutral-300">
      @if (loadingDailyRoute) {
        <p-progressSpinner
          [style]="{ width: '14px', height: '14px' }"
          strokeWidth="4"
        ></p-progressSpinner>
      } @else if (dailyRouteInfo) {
        <fa-icon [icon]="faRoute" class="text-orange-400"></fa-icon>
        <span>
          <strong>{{ dailyRouteInfo.distance_km | number: '1.1-1' }} km</strong>
          -
          <strong>{{ formatDuration(dailyRouteInfo.duration_min) }}</strong>
        </span>
        <!-- Google Maps link for current route -->
        @if (googleMapsUrlCurrentRoute) {
          <a
            [href]="googleMapsUrlCurrentRoute"
            target="_blank"
            rel="noopener noreferrer"
            class="p-1 rounded hover:bg-neutral-700 transition-colors"
            title="Ouvrir l'itinéraire actuel sur Google Maps"
            (click)="$event.stopPropagation()"
          >
            <i class="pi pi-external-link text-[10px] text-orange-400"></i>
          </a>
        }
      }
    </div>
    @if (projectedRouteInfo && dailyRouteInfo) {
      <div class="flex items-center gap-2 text-xs text-emerald-400 mt-1">
        <fa-icon [icon]="faRoute" class="text-emerald-400"></fa-icon>
        <span>
          {{ withLabel }}
          <strong
            >{{ projectedRouteInfo.distance_km | number: '1.1-1' }} km</strong
          >
          -
          <strong>{{ formatDuration(projectedRouteInfo.duration_min) }}</strong>
          <span class="text-emerald-300 ml-1">
            (+{{
              projectedRouteInfo.distance_km - dailyRouteInfo.distance_km
                | number: '1.1-1'
            }}
            km, +{{
              formatDuration(
                projectedRouteInfo.duration_min - dailyRouteInfo.duration_min
              )
            }})
          </span>
        </span>
        <!-- Google Maps link for projected route -->
        @if (googleMapsUrlWithNewItem) {
          <a
            [href]="googleMapsUrlWithNewItem"
            target="_blank"
            rel="noopener noreferrer"
            class="ml-2 p-1 rounded hover:bg-emerald-800/50 transition-colors"
            title="Ouvrir l'itinéraire avec {{ labelNewItem }} sur Google Maps"
            (click)="$event.stopPropagation()"
          >
            <i class="pi pi-external-link text-xs"></i>
          </a>
        }
      </div>
    } @else if (projectedRouteInfo && !dailyRouteInfo) {
      <div class="flex items-center gap-2 text-xs text-emerald-400 mt-1">
        <fa-icon [icon]="faRoute" class="text-emerald-400"></fa-icon>
        <span>
          {{ tripLabel }}
          <strong
            >{{ projectedRouteInfo.distance_km | number: '1.1-1' }} km</strong
          >
          -
          <strong>{{ formatDuration(projectedRouteInfo.duration_min) }}</strong>
        </span>
        <!-- Google Maps link for projected route -->
        @if (googleMapsUrlWithNewItem) {
          <a
            [href]="googleMapsUrlWithNewItem"
            target="_blank"
            rel="noopener noreferrer"
            class="ml-2 p-1 rounded hover:bg-emerald-800/50 transition-colors"
            title="Ouvrir l'itinéraire sur Google Maps"
            (click)="$event.stopPropagation()"
          >
            <i class="pi pi-external-link text-xs"></i>
          </a>
        }
      </div>
    }
  </div>

  <!-- Schedule list -->
  <div class="daily-schedule-list">
    <!-- Start location (home or hotel) -->
    @if (showStartLocation) {
      <div class="daily-schedule-item home-item">
        <div
          class="schedule-item-order home"
          [style.background-color]="technicianColor"
        >
          {{ startLocationEmoji }}
        </div>
        <div class="schedule-item-time">
          @if (startTravelUncounted) {
            <span class="departure-time-display">Hôtel</span>
          } @else if (
            preSelectedSlot?.travelInfo?.isFirstSlotOfDay &&
            preSelectedSlot?.travelInfo?.homeDepartureTime
          ) {
            <span
              class="departure-time-display"
              [class.tight]="preSelectedSlot?.travelInfo?.homeDepartureIsTight"
            >
              {{ formatHomeDepartureTime() }}
            </span>
          } @else if (currentRouteDeparture) {
            <span
              class="departure-time-display"
              [class.tight]="currentRouteDeparture.isTight"
            >
              {{ formatCurrentRouteDepartureTime() }}
            </span>
          } @else {
            Départ
          }
        </div>
        <div class="schedule-item-details">
          <div class="schedule-item-name">{{ startLocationLabel }}</div>
          <div class="schedule-item-city">
            @if (startLocationType === 'hotel') {
              {{ startLocationCity || 'Hébergement' }}
            } @else {
              {{ technicianHomeCity || 'Adresse non renseignée' }}
            }
            @if (startTravelUncounted) {
              <span class="travel-info-line travel-info-uncounted">
                <i class="pi pi-info-circle text-[8px] mr-1"></i>
                Nuit à l'hôtel — trajet du matin à prendre en compte
              </span>
            } @else if (
              preSelectedSlot?.travelInfo?.isFirstSlotOfDay &&
              preSelectedSlot?.travelInfo?.travelFromHome
            ) {
              <span class="travel-info-line">
                <i class="pi pi-arrow-right text-[8px] mr-1"></i>
                {{
                  preSelectedSlot?.travelInfo?.travelFromHome | number: '1.0-0'
                }}
                min vers RDV
              </span>
            } @else if (currentRouteDeparture) {
              <span class="travel-info-line">
                <i class="pi pi-arrow-right text-[8px] mr-1"></i>
                {{ currentRouteDeparture.travelMin | number: '1.0-0' }}
                min vers 1ère inter.
              </span>
            }
          </div>
        </div>
      </div>
    }

    <!-- ============================================================ -->
    <!-- MODE SHIFT-SLOT: Projected schedule with new numbering -->
    <!-- ============================================================ -->
    @if (isShiftSlotSelected && preSelectedSlot) {
      @for (item of getProjectedSchedule(); track item.order) {
        @if (item.type === 'newItem') {
          <!-- New affair inserted at the shifted position -->
          <div class="daily-schedule-item affair-item">
            <div
              class="schedule-item-order"
              style="background-color: #10b981; color: white; font-weight: bold"
            >
              {{ item.order }}
            </div>
            <div class="schedule-item-time text-emerald-400">
              {{ formatPreSelectedSlotTime() }}
              @if (formatPreSelectedSlotEndTime()) {
                - {{ formatPreSelectedSlotEndTime() }}
              }
            </div>
            <div class="schedule-item-details">
              <div class="schedule-item-name text-emerald-400 font-medium">
                {{ newItemName || 'Nouvelle ' + labelNewItem }}
                <span
                  class="inline-flex items-center ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-600 text-white"
                >
                  NOUVEAU
                </span>
              </div>
              <div class="schedule-item-city text-emerald-300">
                {{ newItemCityWithPostalCode }}
              </div>
            </div>
          </div>
        } @else if (item.type === 'shiftedEvent' && item.event) {
          <!-- Shifted event as last numbered point -->
          <div class="daily-schedule-item" style="opacity: 0.6">
            <div
              class="schedule-item-order"
              style="background-color: #9333ea; color: white"
            >
              @if (item.order > 0) {
                {{ item.order }}
              } @else {
                -
              }
            </div>
            <div class="schedule-item-time text-purple-300 line-through">
              {{ formatEventTime(item.event) }}
            </div>
            <div class="schedule-item-details">
              <div
                class="schedule-item-name text-purple-300"
                [title]="item.event.title || item.event.name"
              >
                {{ item.event.title || item.event.name || 'Intervention' }}
                <span
                  class="inline-flex items-center ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-600 text-white"
                >
                  À DÉCALER
                </span>
              </div>
              <div class="schedule-item-city text-purple-400">
                {{ getEventCityWithPostalCode(item.event) }}
              </div>
            </div>
          </div>
        } @else if (item.event) {
          <!-- Existing event (not the shifted one) -->
          <div
            class="daily-schedule-item"
            [class.no-coordinates]="
              !hasValidCoordinatesForEvent(item.event) &&
              !isCalendarEvent(item.event)
            "
          >
            <div
              class="schedule-item-order"
              [style.background-color]="
                isCalendarEvent(item.event)
                  ? item.event.backgroundColor || '#6366f1'
                  : hasValidCoordinatesForEvent(item.event)
                    ? technicianColor
                    : '#666'
              "
            >
              @if (isCalendarEvent(item.event) && item.order === 0) {
                <i class="pi pi-calendar text-xs"></i>
              } @else if (item.order > 0) {
                {{ item.order }}
              } @else {
                <i class="pi pi-exclamation-triangle text-xs"></i>
              }
            </div>
            <div class="schedule-item-time">
              {{ formatEventTime(item.event) }}
            </div>
            <div class="schedule-item-details">
              <div
                class="schedule-item-name"
                [title]="item.event.title || item.event.name"
              >
                {{ getItemLabel(item.event) }}
                @if (
                  item.event.doNotShift === 1 || item.event.doNotShift === true
                ) {
                  <i
                    class="pi pi-lock text-orange-400 ml-1"
                    pTooltip="Ne pas décaler"
                    tooltipPosition="top"
                  ></i>
                }
                @if (
                  !hasValidCoordinatesForEvent(item.event) &&
                  !isCalendarEvent(item.event)
                ) {
                  <i
                    class="pi pi-exclamation-triangle text-yellow-500 ml-1"
                    title="Coordonnées GPS manquantes"
                  ></i>
                }
              </div>
              <div class="schedule-item-city">
                @if (isCalendarEvent(item.event)) {
                  {{
                    item.event.data?.eventType?.name ||
                      getEventCityWithPostalCode(item.event) ||
                      ''
                  }}
                } @else {
                  {{
                    getEventCityWithPostalCode(item.event) ||
                      (hasValidCoordinatesForEvent(item.event)
                        ? ''
                        : 'Coordonnées manquantes')
                  }}
                }
              </div>
            </div>
          </div>
        }
      }
    } @else {
      <!-- ============================================================ -->
      <!-- MODE NORMAL: Standard schedule with insertion -->
      <!-- ============================================================ -->
      @for (
        event of getSortedDailyEvents();
        track event.id || $index;
        let i = $index
      ) {
        <!-- Insert new item before this event if it should come before based on time -->
        @if (preSelectedSlot && shouldInsertNewItemBefore(event, i === 0)) {
          <div class="daily-schedule-item affair-item">
            <div
              class="schedule-item-order affair"
              style="background-color: #10b981"
            >
              <i class="pi pi-plus text-xs"></i>
            </div>
            <div class="schedule-item-time">
              {{ formatPreSelectedSlotTime() }}
              @if (formatPreSelectedSlotEndTime()) {
                - {{ formatPreSelectedSlotEndTime() }}
              }
            </div>
            <div class="schedule-item-details">
              <div class="schedule-item-name text-emerald-400">
                {{ newItemName || 'Nouvelle ' + labelNewItem }}
              </div>
              <div class="schedule-item-city text-emerald-300">
                {{ newItemCityWithPostalCode }}
              </div>
            </div>
          </div>
        }
        <div
          class="daily-schedule-item"
          [class.no-coordinates]="
            !hasValidCoordinatesForEvent(event) && !isCalendarEvent(event)
          "
        >
          <div
            class="schedule-item-order"
            [style.background-color]="
              isCalendarEvent(event)
                ? event.backgroundColor || '#6366f1'
                : hasValidCoordinatesForEvent(event)
                  ? technicianColor
                  : '#666'
            "
          >
            @if (isCalendarEvent(event) && !isOnMap(event)) {
              <i class="pi pi-calendar text-xs"></i>
            } @else if (isOnMap(event)) {
              {{ getRouteOrder(i) }}
            } @else {
              <i class="pi pi-exclamation-triangle text-xs"></i>
            }
          </div>
          <div class="schedule-item-time">
            {{ formatEventTime(event) }}
          </div>
          <div class="schedule-item-details">
            <div class="schedule-item-name" [title]="event.title || event.name">
              {{ getItemLabel(event) }}
              @if (event.doNotShift === 1 || event.doNotShift === true) {
                <i
                  class="pi pi-lock text-orange-400 ml-1"
                  pTooltip="Ne pas décaler"
                  tooltipPosition="top"
                ></i>
              }
              @if (
                !hasValidCoordinatesForEvent(event) && !isCalendarEvent(event)
              ) {
                <i
                  class="pi pi-exclamation-triangle text-yellow-500 ml-1"
                  title="Coordonnées GPS manquantes"
                ></i>
              }
            </div>
            <div class="schedule-item-city">
              @if (isCalendarEvent(event)) {
                {{
                  event.data?.eventType?.name ||
                    getEventCityWithPostalCode(event) ||
                    ''
                }}
              } @else {
                {{
                  getEventCityWithPostalCode(event) ||
                    (hasValidCoordinatesForEvent(event)
                      ? ''
                      : 'Coordonnées manquantes')
                }}
              }
            </div>
          </div>
        </div>
      }
      <!-- Insert new item at end if it comes after all existing events -->
      @if (preSelectedSlot && shouldInsertNewItemAtEnd()) {
        <div class="daily-schedule-item affair-item">
          <div
            class="schedule-item-order affair"
            style="background-color: #10b981"
          >
            <i class="pi pi-plus text-xs"></i>
          </div>
          <div class="schedule-item-time">
            {{ formatPreSelectedSlotTime() }}
            @if (formatPreSelectedSlotEndTime()) {
              - {{ formatPreSelectedSlotEndTime() }}
            }
          </div>
          <div class="schedule-item-details">
            <div class="schedule-item-name text-emerald-400">
              {{ newItemName || 'Nouvelle ' + labelNewItem }}
            </div>
            <div class="schedule-item-city text-emerald-300">
              {{ newItemCityWithPostalCode }}
            </div>
          </div>
        </div>
      }
    }
    <!-- End location (home or hotel) -->
    @if (showEndLocation) {
      <div class="daily-schedule-item home-item">
        <div
          class="schedule-item-order home"
          [style.background-color]="technicianColor"
        >
          {{ endLocationEmoji }}
        </div>
        <div class="schedule-item-time">
          @if (endTravelUncounted) {
            <span class="departure-time-display">Hôtel</span>
          } @else {
            @if (getReturnInfoToDisplay(); as returnInfo) {
              <span
                class="departure-time-display"
                [class.text-emerald-400]="returnInfo.isProjected"
              >
                {{ returnInfo.returnTime }}
              </span>
            } @else {
              Retour
            }
          }
        </div>
        <div class="schedule-item-details">
          <div class="schedule-item-name">{{ endLocationLabel }}</div>
          <div class="schedule-item-city">
            @if (endLocationType === 'hotel') {
              {{ endLocationCity || 'Hébergement' }}
            } @else {
              {{ technicianHomeCity || 'Adresse non renseignée' }}
            }
            @if (endTravelUncounted) {
              <span class="travel-info-line travel-info-uncounted">
                <i class="pi pi-info-circle text-[8px] mr-1"></i>
                Nuit à l'hôtel — trajet du soir à prendre en compte
              </span>
            } @else {
              @if (getReturnInfoToDisplay(); as returnInfo) {
                <span
                  class="travel-info-line"
                  [class.text-emerald-300]="returnInfo.isProjected"
                >
                  <i class="pi pi-arrow-left text-[8px] mr-1"></i>
                  {{ returnInfo.travelMin | number: '1.0-0' }}
                  min depuis
                  {{ returnInfo.isProjected ? 'RDV' : 'dernière inter.' }}
                </span>
              }
            }
          </div>
        </div>
      </div>
    }
  </div>
</div>
